/**
 * Workspace scope store.
 *
 * An external store, for the same reason session state is one: route guards and
 * navigation composition need a SYNCHRONOUS read, outside the component tree.
 *
 * The store enforces the switch protocol's cache half — on a scope change it
 * bumps a generation counter that every tenant-scoped query keys on, so a
 * response from the previous scope cannot land in the current one. The query
 * client is remounted alongside it; this counter is the belt to that braces.
 */
import type { WorkspaceResolution, WorkspaceScope } from '@/app/workspace/types';

type Listener = (resolution: WorkspaceResolution, generation: number) => void;

let resolution: WorkspaceResolution = { status: 'error', message: 'Workspace not resolved yet' };
let currentScope: WorkspaceScope | null = null;
let resolvedAt = 0;
let generation = 0;

const listeners = new Set<Listener>();

function publish(next: WorkspaceResolution, scope: WorkspaceScope | null): void {
  // A change of SCOPE bumps the generation. A refresh of the SAME scope does
  // not — re-fetching a workspace every 30 seconds must not invalidate every
  // query in the application.
  if (scope?.tenant.id !== currentScope?.tenant.id) {
    generation += 1;
  }
  resolution = next;
  currentScope = scope;
  resolvedAt = Date.now();

  for (const listener of listeners) {
    listener(resolution, generation);
  }
}

export function getWorkspaceResolution(): WorkspaceResolution {
  return resolution;
}

export function getWorkspaceScope(): WorkspaceScope | null {
  return currentScope;
}

/** Include this in every tenant-scoped query key. */
export function getScopeGeneration(): number {
  return generation;
}

export function getScopeResolvedAt(): number {
  return resolvedAt;
}

export function setWorkspaceScope(next: WorkspaceResolution, scope: WorkspaceScope | null): void {
  publish(next, scope);
}

export function subscribeWorkspace(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function resetWorkspaceTestState(): void {
  resolution = { status: 'error', message: 'Workspace not resolved yet' };
  currentScope = null;
  resolvedAt = 0;
  generation = 0;
  listeners.clear();
}